import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import logo_white from '../assets/logo_white.png';
import { brand } from '../thema';

const STORAGE_KEY = 'reband-intro-shown';
const TAGLINE = 'Architect × AI.';

const gridIn = keyframes`
  from { opacity: 0; background-size: 24px 24px; }
  to { opacity: 1; background-size: 56px 56px; }
`;
const reveal = keyframes`
  from { clip-path: inset(0 100% 0 0); }
  to { clip-path: inset(0 0 0 0); }
`;
const lineGrow = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;
const typing = keyframes`
  from { width: 0; }
  to { width: ${TAGLINE.length}ch; }
`;
const caret = keyframes`
  50% { border-color: transparent; }
`;

// 1セッションにつき1回だけ表示。動きを減らす設定の場合は表示しない
const shouldShow = () => {
    try {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
        return sessionStorage.getItem(STORAGE_KEY) !== '1';
    } catch {
        return false;
    }
};

const Intro = () => {
    const [visible, setVisible] = useState(shouldShow);
    const [leaving, setLeaving] = useState(false);

    useEffect(() => {
        document.documentElement.classList.remove('intro-pending');
    }, []);

    useEffect(() => {
        if (!visible) return;
        try {
            sessionStorage.setItem(STORAGE_KEY, '1');
        } catch {
            // ストレージが使えない環境では毎回表示される
        }
        document.body.style.overflow = 'hidden';
        const leaveTimer = setTimeout(() => setLeaving(true), 2400);
        const hideTimer = setTimeout(() => setVisible(false), 3100);
        return () => {
            clearTimeout(leaveTimer);
            clearTimeout(hideTimer);
            document.body.style.overflow = '';
        };
    }, [visible]);

    if (!visible) return null;

    return (
        <Box
            onClick={() => setLeaving(true)}
            onTransitionEnd={() => leaving && setVisible(false)}
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 2000,
                backgroundColor: '#000000',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transform: leaving ? 'translateY(-100%)' : 'none',
                transition: 'transform 0.7s cubic-bezier(0.76, 0, 0.24, 1)',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
                    backgroundSize: '56px 56px',
                    backgroundPosition: 'center',
                    animation: `${gridIn} 1.2s ease-out both`,
                },
            }}
        >
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box
                    component="img"
                    src={logo_white}
                    alt="Reband"
                    sx={{
                        width: { xs: 180, md: 240 },
                        height: 'auto',
                        animation: `${reveal} 0.9s cubic-bezier(0.65, 0, 0.35, 1) 0.3s both`,
                    }}
                />
                <Box
                    sx={{
                        mt: 2.5,
                        width: { xs: 120, md: 160 },
                        height: '1px',
                        backgroundColor: brand.accent,
                        transformOrigin: 'left',
                        animation: `${lineGrow} 0.6s ease-out 1s both`,
                    }}
                />
                <Box
                    sx={{
                        mt: 2.5,
                        fontFamily: brand.fontMono,
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        color: brand.accent,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        borderRight: `2px solid ${brand.accent}`,
                        width: 0,
                        animation: `${typing} 0.8s steps(${TAGLINE.length}) 1.4s forwards, ${caret} 0.8s step-end infinite`,
                    }}
                >
                    {TAGLINE}
                </Box>
            </Box>
        </Box>
    );
};

export default Intro;
